import {getQuotaReservations} from '@account/api';
import {groupReservationsByCollection} from '@account/utils/az-geometry';
import {useQuery} from '@tanstack/react-query';

// Upper bound on reservation rows pulled. The client colors reserved AOIs by
// matching each collection's reserved feature hashes against the MVT tiles, so
// unlike a server-side tile filter we need the actual refs, not just the
// distinct collections. This covers all but the largest handful of accounts;
// beyond it `enumerated` is false: some reserved AOIs go uncolored and
// `reservedDrawn` is a lower bound rather than the full reserved count.
export const ENUMERATE_CAP = 20000;

const staleTime = 5 * 60 * 1000;

const EMPTY = {
  collections: [],
  reservedDrawn: 0,
  enumerated: true,
};

// Stable selector so React Query memoizes the derivation and only recomputes it
// when the raw query data changes (an inline function would recompute every
// render). Turns the reservation rows into the per-collection layers to draw
// (each collection with its set of reserved feature hashes) plus the count to
// show.
//
// meta.count is NOT the reserved count: the list endpoint filters by product
// only, so it counts every reservation state (reserved + cancelled + pending),
// while we draw only `reserved`. So the displayed number is `reservedDrawn` (the
// distinct reserved hashes we found); meta.count is used solely to tell whether
// we pulled every row (`enumerated`) — when we didn't, reservedDrawn is a lower
// bound.
function selectReservedLayer(data) {
  const rowCount = data?.meta?.count ?? 0;
  const rows = data?.results ?? [];
  const collections = groupReservationsByCollection(rows).map(g => ({
    collectionId: g.collectionId,
    dataset: g.dataset,
    reservedHashids: g.itemIds,
  }));
  const reservedDrawn = collections.reduce(
    (sum, c) => sum + c.reservedHashids.size,
    0
  );
  return {
    collections,
    reservedDrawn,
    // We pulled every reservation row, so `reservedDrawn` is the complete
    // reserved count. False = we hit the cap and reservedDrawn is a lower bound.
    enumerated: rows.length >= rowCount,
  };
}

/**
 * Resolve a workspace product access's reserved AOIs into the collections to
 * draw plus, per collection, the set of reserved feature hashes. The map renders
 * each collection's MVT and a style function paints only the features whose
 * `pl:ref` hash is in that set — no `?_relation` tile filter, no per-feature
 * geometry fetch.
 */
export function useReservedAoiLayer({productAccessId, enabled = true} = {}) {
  const query = useQuery({
    queryKey: ['quotaReservations', productAccessId, ENUMERATE_CAP],
    queryFn: () =>
      getQuotaReservations({
        productAccessId,
        limit: ENUMERATE_CAP,
        fields: 'collection_id,aoi_ref,state',
      }),
    enabled: enabled && !!productAccessId,
    staleTime,
    select: selectReservedLayer,
  });

  const {collections, reservedDrawn, enumerated} =
    (enabled && query.data) || EMPTY;

  return {
    collections,
    reservedDrawn,
    enumerated,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}
